import { default as MuiAvatar, AvatarProps as MuiAvatarProps } from '@mui/material/Avatar'
import { colors } from './colors'
import { theme } from '../../theme'
import React, { FC } from 'react'
export type AvatarProps = Pick<MuiAvatarProps, 'src' | 'srcSet'> & {
  alt: string
  size?: 'medium' | 'small' | 'xxLarge' | 'xLarge' | 'large'
  variant?: 'circular' | 'rounded' | 'square'
  icon?: JSX.Element
  disabled?: boolean
}

const COMBOS = [
  { backgroundColor: colors.purple1, color: colors.purple5 },
  { backgroundColor: colors.pumpkin1, color: colors.pumpkin5 },
  { backgroundColor: colors.gold1, color: colors.gold5 },
  {
    backgroundColor: colors.flowblue1,
    color: colors.flowblue5
  },
  { backgroundColor: colors.sky1, color: colors.sky5 },
  {
    backgroundColor: colors.raspberry1,
    color: colors.raspberry5
  },
  {
    backgroundColor: colors.torqoise1,
    color: colors.torqoise5
  },
  { backgroundColor: colors.fuschia1, color: colors.fuschia5 },
  { backgroundColor: colors.jade1, color: colors.jade5 }
]

export const getColors = (
  name: string,
  disabled = false
): { backgroundColor: string; color: string } => {
  //gets the base 64 integer equivalent of the name string, converts it to a number between 0 and 8
  if (disabled)
    return {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.background.default
    }
  const colorsIndex = parseInt(name, 36) % COMBOS.length
  return COMBOS[colorsIndex] ?? COMBOS[0]
}

export function stringAvatar(name: string): MuiAvatarProps {
  const splitName = name ? name.trim().split(' ') : ['']
  return {
    children: (name && splitName.length > 1
      ? `${splitName[0]?.[0] || ''}${splitName[splitName.length - 1]?.[0] || ''}`
      : name?.[0] || ''
    ).toUpperCase()
  }
}

/**
 * Avatars are found throughout material design with uses in everything from tables to dialog menus.
 * If no photo is supplied Avatar with take the initials from the alt text
 * More info can be found at: https://mui.com/material-ui/react-avatar
 */

export const Avatar: FC<AvatarProps> = ({
  alt,
  size = 'medium',
  variant = 'circular',
  icon,
  disabled,
  ...rest
}) => {
  if (!alt?.length) console.warn('Avatars are contextual and require an alt message')

  const sizes = {
    xxLarge: '196px',
    xLarge: '64px',
    large: '48px',
    medium: '40px',
    small: '32px'
  }
  const fontSizes = {
    xxLarge: '3.25rem',
    xLarge: '1.75rem',
    large: '1.5rem',
    medium: '1.25rem',
    small: '1rem'
  }

  const injected = { ...rest, ...{ children: icon } }
  if (!rest.src) {
    return (
      <MuiAvatar
        component="div"
        {...rest}
        alt={alt}
        variant={variant}
        sx={theme => ({
          width: sizes[size],
          height: sizes[size],
          fontSize: fontSizes[size],
          ...getColors(alt, disabled),
          border: disabled ? `1px dashed ${theme.palette.action.disabled}` : undefined
        })}
        aria-hidden="true"
        {...stringAvatar(alt)}
      />
    )
  }
  return (
    <MuiAvatar
      {...injected}
      alt={alt}
      variant={variant}
      sx={theme => ({
        width: sizes[size],
        height: sizes[size],
        bgcolor: theme.palette.primary.main
      })}
    />
  )
}
